/**
 * 资料库服务（PDF 清单）
 * ------------------------------------------------------------
 * 数据分两层：
 *   1) data/library.json —— 随项目发布的「事实清单」：哪些 PDF 已经进了项目、
 *      有没有文字层、识别到哪一步。由开发者/导入流程维护，前端只读。
 *   2) IndexedDB 的 LIBRARY 集合 —— 用户在页面上点「确认入库」后写入的状态，
 *      会覆盖文件里的 confirmed 字段（和教材内容一样走「存储覆盖文件」策略）。
 *
 * 为什么不在运行时扫描目录：静态托管没有目录列表能力，扫描在本地开发能用、
 * 部署后就失效。显式清单在任何环境都一样。
 */
import { getStorage } from '../core/storage/index.js';
import { COLLECTIONS } from '../core/schema.js';

const cache = { loaded: false, base: null };

function baseUrl() {
  if (cache.base) return cache.base;
  const inline = typeof window !== 'undefined' ? window.__INLINE_DATA__ : null;
  if (inline?.['data/library.json']) {
    cache.base = inline['data/library.json'];
    return cache.base;
  }
  const base = (typeof document !== 'undefined' && document.baseURI) || location.href;
  cache.base = new URL('data/library.json', base).href;
  return cache.base;
}

/** 读取资料库清单（文件 + 本机确认状态合并） */
export async function loadLibrary() {
  let fileData;
  const src = baseUrl();
  if (typeof src === 'string' && src.startsWith('data/')) {
    fileData = src;                       // 单文件版：直接是对象
  } else {
    const res = await fetch(src, { cache: 'no-cache' });
    if (!res.ok) throw new Error(`加载资料库清单失败（${res.status}）`);
    fileData = await res.json();
  }

  const storage = getStorage();
  let overrides = [];
  try {
    overrides = await storage.list(COLLECTIONS.LIBRARY || 'library');
  } catch { overrides = []; }
  const map = new Map(overrides.map(o => [o.id, o]));

  return {
    ...fileData,
    items: (fileData.items || []).map(it => {
      const ov = map.get(it.id);
      return ov ? { ...it, ...ov, source: 'store' } : { ...it, source: 'file' };
    }),
  };
}

/** 标记某份资料已确认（写入本机，不修改 data/library.json） */
export async function markConfirmed(id, patch = {}) {
  const storage = getStorage();
  const col = COLLECTIONS.LIBRARY || 'library';
  const existing = (await storage.list(col)).find(x => x.id === id);
  const row = {
    ...(existing || { id }),
    id,
    confirmed: true,
    confirmedAt: new Date().toISOString(),
    ...patch,
  };
  if (storage.put) await storage.put(col, row);
  else await storage.add(col, row);
  return row;
}
